<script setup lang="ts">
const milestones = [
  {
    year: 'Okt 2025',
    title: 'Het Idee',
    description: 'Een gek idee ontstond: wat als kinderen hun eigen avonturen kunnen bouwen in speelse kampen?',
    icon: 'bx-bulb',
    color: '#F27063',
    position: 'left',
  },
  {
    year: 'Nov 2025',
    title: 'Opstart',
    description: 'De oprichting van \'t SpeelFabrik VZW uit Zemst. Een non-profit organisatie met een missie.',
    icon: 'bx-rocket',
    color: '#8FB5D9',
    position: 'right',
  },
  {
    year: 'Jan 2026',
    title: 'Eerste Zwemles',
    description: 'De allereerste zwemles voor kinderen. Een belangrijke stap in ons avontuur.',
    icon: 'bx-swim',
    color: '#FFD93D',
    position: 'left',
  },
  {
    year: 'Apr 2026',
    title: 'Eerste Kamp',
    description: 'Ons allereerste kamp. Samen vliegen we erin!',
    icon: 'bx-home-smile',
    color: '#77882B',
    position: 'right',
  },
]
</script>

<template>
  <div
    id="timeline"
    class="timeline-section"
  >
    <VContainer>
      <div class="section-header">
        <div class="section-badge">
          <VIcon icon="bx-time" />
          <span>Onze Tijdslijn</span>
        </div>
        <h2 class="section-title">
          De reis van 't SpeelFabrik
        </h2>
        <p class="section-description">
          Van een gek idee tot een volwaardig avontuur. Ontdek hoe 't SpeelFabrik tot leven kwam.
        </p>
      </div>

      <div class="timeline">
        <div class="timeline-line" />

        <div
          v-for="(milestone, index) in milestones"
          :key="index"
          class="timeline-item"
          :class="milestone.position"
          :style="{ '--item-delay': `${index * 0.2}s` }"
        >
          <div class="timeline-content">
            <div class="milestone-card">
              <div
                class="milestone-icon"
                :style="{
                  background: milestone.color,
                }"
              >
                <VIcon
                  :icon="milestone.icon"
                  size="32"
                  color="white"
                />
              </div>

              <div class="milestone-year">
                {{ milestone.year }}
              </div>

              <h3 class="milestone-title">
                {{ milestone.title }}
              </h3>

              <p class="milestone-description">
                {{ milestone.description }}
              </p>

              <div
                class="milestone-accent"
                :style="{ background: milestone.color }"
              />
            </div>
          </div>

          <div
            class="timeline-dot"
            :style="{ borderColor: milestone.color }"
          >
            <div
              class="dot-inner"
              :style="{ background: milestone.color }"
            />
          </div>
        </div>
      </div>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.timeline-section {
  position: relative;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  padding-block: 6rem;
}

.section-header {
  margin-block-end: 5rem;
  text-align: center;

  .section-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    background: rgba(242, 112, 99, 10%);
    color: #f27063;
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    gap: 0.5rem;
    letter-spacing: 0.5px;
    margin-block-end: 1.5rem;
    padding-block: 0.5rem;
    padding-inline: 1.5rem;
    text-transform: uppercase;

    .v-icon {
      color: currentcolor;
    }
  }

  .section-title {
    margin: 0;
    color: rgb(var(--v-theme-on-surface));
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    line-height: 1.2;
    margin-block-end: 1rem;
  }

  .section-description {
    color: #546e7a;
    font-size: 1.125rem;
    line-height: 1.6;
    margin-inline: auto;
    max-inline-size: 600px;
  }
}

.timeline {
  position: relative;
  margin-inline: auto;
  max-inline-size: 1200px;
  padding-block: 2rem;
}

.timeline-line {
  position: absolute;
  border-radius: 4px;
  background: rgba(242, 112, 99, 20%);
  inline-size: 4px;
  inset-block: 0;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.timeline-dot {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  align-self: start;
  justify-content: center;
  border: 4px solid;
  border-radius: 50%;
  background: white;
  block-size: 60px;
  inline-size: 60px;
  margin-block-start: 2rem;

  .dot-inner {
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
    block-size: 24px;
    inline-size: 24px;
  }
}

.milestone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  block-size: 80px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 15%);
  inline-size: 80px;
  margin-block-end: 1.5rem;
  transition: transform 0.3s ease;
}

.milestone-card {
  position: relative;
  padding: 2rem;
  border-radius: 20px;
  background: white;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 8%);
  max-inline-size: 450px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 12%);
    transform: translateY(-4px);

    .milestone-icon {
      transform: scale(1.1) rotate(5deg);
    }
  }
}

.timeline-item {
  position: relative;
  display: grid;
  animation: fade-in 0.8s ease-out forwards;
  animation-delay: var(--item-delay);
  gap: 2rem;
  grid-template-columns: 1fr 60px 1fr;
  margin-block-end: 4rem;
  opacity: 0;

  &.left {
    .timeline-content {
      grid-column: 1;
      text-align: end;
    }

    .timeline-dot {
      grid-column: 2;
    }

    .milestone-card {
      margin-inline-start: auto;
    }
  }

  &.right {
    .timeline-content {
      grid-column: 3;
      text-align: start;
    }

    .timeline-dot {
      grid-column: 2;
    }

    .milestone-card {
      margin-inline-end: auto;
    }
  }
}

.milestone-year {
  color: #f27063;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  margin-block-end: 0.5rem;
  text-transform: uppercase;
}

.milestone-title {
  color: #2c3e50;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.3;
  margin-block-end: 1rem;
}

.milestone-description {
  color: #546e7a;
  font-size: 1rem;
  line-height: 1.6;
}

.milestone-accent {
  position: absolute;
  block-size: 4px;
  border-end-start-radius: 4px;
  border-start-start-radius: 4px;
  inline-size: 60px;
  inset-block-end: 0;
  inset-inline-start: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.8;
    transform: scale(1.2);
  }
}

@media (max-width: 960px) {
  .timeline-section {
    padding-block: 4rem;
  }

  .timeline-line {
    inset-inline-start: 30px;
  }

  .timeline-item {
    gap: 1.5rem;
    grid-template-columns: 60px 1fr;

    &.left,
    &.right {
      .timeline-content {
        grid-column: 2;
        text-align: start;
      }

      .timeline-dot {
        grid-column: 1;
      }

      .milestone-card {
        margin-inline: 0;
      }
    }
  }

  .milestone-card {
    max-inline-size: 100%;
  }
}
</style>
